import { LayoutGraph } from './types';

const LEVEL_MARGIN = 50;

/**
 * Calculates X positions for nodes based on their levels.
 *
 * Nodes are positioned by their right edge to prevent overlapping.
 * Level 0 (roots) are positioned with right edge at 0.
 * Higher levels move to the left with spacing based on cumulative widths and margins.
 */
export function calculateXPositions(
    graph: LayoutGraph,
    levels: Map<number, number>,
    levelWidths: Map<number, number>,
): Map<number, number> {
    const xPositions = new Map<number, number>();
    const nodeMap = new Map(graph.nodes.map(node => [node.id, node]));

    // Find max level to iterate through all levels
    const maxLevel = Math.max(...Array.from(levels.values()));

    // Calculate cumulative widths for each level
    const cumulativeWidths = new Map<number, number>();
    let cumulative = 0;

    for (let level = 0; level <= maxLevel; level++) {
        cumulativeWidths.set(level, cumulative);
        const width = levelWidths.get(level) || 0;
        cumulative += width;
    }

    // Calculate x position for each node (positioned by right edge)
    for (const [nodeId, level] of levels.entries()) {
        const node = nodeMap.get(nodeId);
        if (!node) continue;

        const cumulativeWidth = cumulativeWidths.get(level) || 0;
        const marginOffset = level * LEVEL_MARGIN;

        // Calculate the right edge position for this level
        let rightEdge = -1 * (cumulativeWidth + marginOffset);

        // Avoid -0 (convert to positive 0)
        if (rightEdge === 0) rightEdge = 0;

        // Position node so its right edge aligns with the level's right edge
        const x = rightEdge - node.width;

        xPositions.set(nodeId, x);
    }

    return xPositions;
}
